import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2, Timer } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DAY_OPTIONS = [14, 30, 60, 90];

export const StaleCompaniesPanel = ({ onChanged }) => {
  const [days, setDays] = useState(30);
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");

  const load = useCallback(async (d) => {
    try {
      const { data } = await api.get("/admin/stale-companies", { params: { days: d } });
      setList(data.companies);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/stale-companies/purge", { days, confirm });
      toast.success(`${data.purged_count} oanvända konton raderade`);
      setOpen(false);
      setConfirm("");
      await load(days);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const count = list?.length ?? 0;

  return (
    <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4" data-testid="admin-stale-panel">
      <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-800">
        <Timer className="h-4 w-4 text-slate-400" /> Rensa oanvända konton
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        Listar företag som registrerat sig men <b>aldrig använt tjänsten</b>: inga arbetsordrar,
        kunder, prislisteartiklar eller frånvaro, inga extra användare, inget abonnemang och ingen
        gratis åtkomst. Interna kontot, demokontot och alla konton med data skyddas alltid.
      </p>

      <div className="mt-3 flex flex-wrap gap-2" data-testid="stale-days-tabs">
        {DAY_OPTIONS.map((d) => (
          <button
            key={d}
            data-testid={`stale-days-${d}`}
            onClick={() => setDays(d)}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${
              days === d
                ? "border-blue-600 bg-blue-600 text-white"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
            }`}
          >
            Äldre än {d} dagar
          </button>
        ))}
      </div>

      {list === null ? (
        <Loader2 className="mt-4 h-5 w-5 animate-spin text-slate-400" />
      ) : count === 0 ? (
        <p className="mt-3 text-sm font-medium text-slate-600" data-testid="stale-empty">
          Inga oanvända konton äldre än {days} dagar.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100 rounded-md border border-slate-200" data-testid="stale-list">
          {list.map((c) => (
            <li key={c.key} data-testid={`stale-company-${c.key}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 p-3 text-sm">
              <span className="font-semibold text-slate-900">{c.name}</span>
              {c.org_no && <span className="text-slate-500">{c.org_no}</span>}
              <span className="text-slate-600">{(c.admins?.[0]?.email) || "ingen admin"}</span>
              <span className="ml-auto text-slate-500">
                registrerat {(c.created_at || "").slice(0, 10)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <Button
        data-testid="stale-purge-button"
        disabled={busy || count === 0}
        onClick={() => { setConfirm(""); setOpen(true); }}
        className="mt-3 h-11 bg-red-600 text-white transition-colors duration-200 hover:bg-red-700"
      >
        <Trash2 className="mr-1.5 h-4 w-4" /> Radera {count} oanvända konton
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-white" data-testid="stale-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Radera {count} oanvända konton permanent?</AlertDialogTitle>
            <AlertDialogDescription>
              Företagen och deras administratörskonton raderas helt. Åtgärden går inte att ångra.
              Endast konton i listan ovan berörs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">
              Skriv RENSA för att bekräfta
            </Label>
            <Input
              data-testid="stale-confirm-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="RENSA"
              className="mt-1 h-10"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="stale-purge-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              data-testid="stale-purge-ok"
              disabled={busy || confirm.trim().toUpperCase() !== "RENSA"}
              onClick={(e) => { e.preventDefault(); purge(); }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ja, radera"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
